import {
	AddNewText,
	CloseIconButton,
	EntityButton,
	EntitySelectorMenuDropdown,
	InputWrapper,
	NoResultsDiv,
	TagContentDiv,
	TagItemDiv,
	Tags,
	TagsContainer,
	TagsDropdownContainer,
	UserSelectionText,
	VirtualListContainer,
	VirtualListItem,
	VirtualListWrapper,
} from '@dealstack/frontend/app/pages/Valuations/components/EntitySelectorDropdown/styled';
import { getEntityClassifierBadgeConfig } from '@dealstack/frontend/app/pages/Valuations/shared/entityClassiferBadgeConfig';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { LegalPersonTags, PersonSummary } from '@dealstack/shared/lib/generated';
import { Badge, Checkbox, Flex, Input, Menu, Text } from '@mantine/core';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useEffect, useRef, useState } from 'react';
import { filter, map, intersection, isEmpty } from 'lodash';
import { TruncateMiddle } from '@dealstack/frontend/app/pages/Valuations/components/EntitySelectorDropdown/TruncateMiddle/TruncateMiddle';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { VALUATION_ELEMENT_ID } from '@dealstack/shared/tests/testElementIds';

const ENTITY_SELECTOR_LIST_ITEM_HEIGHT = 42;

export interface PersonOption {
	personId: string;
	mipId?: string;
	label: string;
	tags: LegalPersonTags[];
}

interface EntitySelectorDropdownProps {
	selectedPersonId: string | undefined;
	selectedMipId: string | undefined;
	personOptions: PersonOption[];
	selectedEntity: PersonSummary | undefined;
	availableTags: LegalPersonTags[];
	isLoadingPerson: boolean;
	loadPerson: (params: { personId: string; mipId?: string }) => void;
}

export function EntitySelectorDropdown({
	selectedPersonId,
	selectedMipId,
	personOptions,
	selectedEntity,
	availableTags,
	isLoadingPerson,
	loadPerson,
}: EntitySelectorDropdownProps) {
	const [personsMenuOpened, setPersonsMenuOpened] = useState(false);
	const [tagsMenuOpened, setTagsMenuOpened] = useState(false);
	const [selectedTags, setSelectedTags] = useState<Set<LegalPersonTags>>(new Set());
	const [searchTerm, setSearchTerm] = useState('');
	const personDropdownRef = useRef<HTMLDivElement>(null);
	const personDropdownTargetRef = useRef<HTMLDivElement>(null);
	const tagsDropdownRef = useRef<HTMLDivElement>(null);
	const tagsDropdownTargetRef = useRef<HTMLDivElement>(null);
	const tCapManage = useTranslationCapManage();

	const processedSearchTerm = searchTerm.trim().toLowerCase();
	const selectedTagsArray = Array.from(selectedTags);

	const personListItems = map(
		filter(personOptions, (person) => {
			const matchesSearchTerm = person.label.toLowerCase().includes(processedSearchTerm);

			return (
				matchesSearchTerm &&
				(isEmpty(selectedTags) ||
					!!intersection(selectedTagsArray, person.tags ?? []).length)
			);
		}),
		(person) => ({
			label: person.label,
			personId: person.personId,
			mipId: person.mipId,
			selected:
				person.personId === selectedPersonId &&
				(person.mipId ?? undefined) === selectedMipId,
			tags: person.tags,
		})
	);

	const virtualListRef = useRef<HTMLDivElement>(null);

	const virtualizer = useVirtualizer({
		count: personListItems.length,
		getScrollElement: () => virtualListRef.current,
		estimateSize: () => ENTITY_SELECTOR_LIST_ITEM_HEIGHT,
		overscan: 10,
	});

	const selectedPersonName =
		selectedEntity?.displayName ?? (isLoadingPerson ? 'Loading...' : 'Unknown');
	const selectedGroupName = selectedEntity?.groups?.find(
		(group) => group.id === selectedMipId
	)?.name;
	const selectedEntityName = selectedGroupName
		? `${selectedPersonName} (${selectedGroupName})`
		: selectedPersonName;

	const handleClickOutsideDropdowns = useCallback(
		(event: MouseEvent) => {
			const isClickInsidePersonDropdown =
				(personDropdownRef.current &&
					personDropdownRef.current.contains(event.target as Node)) ||
				(personDropdownTargetRef.current &&
					personDropdownTargetRef.current.contains(event.target as Node));

			if (!isClickInsidePersonDropdown && personsMenuOpened) {
				setPersonsMenuOpened(false);
			}

			const isClickInsideTagsDropdown =
				(tagsDropdownRef.current &&
					tagsDropdownRef.current.contains(event.target as Node)) ||
				(tagsDropdownTargetRef.current &&
					tagsDropdownTargetRef.current.contains(event.target as Node));

			if (!isClickInsideTagsDropdown && tagsMenuOpened) {
				setTagsMenuOpened(false);
			}
		},
		[personsMenuOpened, tagsMenuOpened]
	);

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutsideDropdowns);

		return () => document.removeEventListener('mousedown', handleClickOutsideDropdowns);
	}, [handleClickOutsideDropdowns]);

	const handlePersonSelect = (person: { personId: string; mipId?: string }) => {
		loadPerson({ personId: person.personId, mipId: person.mipId });
	};

	const handleTagSelect = (tag: LegalPersonTags, isChecked: boolean) => {
		if (isChecked) {
			const newSelectedTags = new Set(selectedTags);
			newSelectedTags.add(tag);
			setSelectedTags(newSelectedTags);
		} else {
			const newSelectedTags = new Set(selectedTags);
			newSelectedTags.delete(tag);
			setSelectedTags(newSelectedTags);
		}
	};

	return (
		<Menu
			opened={personsMenuOpened}
			position="bottom-start"
			transitionProps={{
				// The mantine menu has an animation transition that causes the virtualizer to not be measured correctly on mount.
				// This manually triggers the virtualizer to measure the list items, after the transition is started, and the element is visible.
				onEntered: () => {
					virtualizer.measure();
				},
			}}
		>
			<Menu.Target>
				<Flex
					data-testid={VALUATION_ELEMENT_ID.SOURCES_AND_USES_TITLE}
					align="center"
					justify="center"
					gap="xs"
					maw="35vw"
					style={{
						cursor: 'pointer',
						userSelect: 'none',
					}}
					ref={personDropdownTargetRef}
					onClick={() => {
						setPersonsMenuOpened((p) => !p);
					}}
				>
					<TruncateMiddle text={selectedEntityName} size="B3Bold" />
					<Icon variant="caret-down" size="vlg" />
				</Flex>
			</Menu.Target>
			<EntitySelectorMenuDropdown styles={{ dropdown: { width: '24rem' } }}>
				<div
					ref={personDropdownRef}
					style={{
						boxShadow: `0px 16px 32px 0px ${defaultTheme.color.shadow.grey[0]}`,
						width: '100%',
					}}
				>
					<div>
						<InputWrapper>
							<Input
								placeholder="Search"
								size="sm"
								leftSection={<Icon variant="search-v3" size="md" />}
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.currentTarget.value)}
							/>
						</InputWrapper>
					</div>

					<TagsContainer>
						<div>
							<Text size="B6Medium" fz="sm" c="gray.9">
								Filter by
							</Text>
						</div>

						<Menu position="bottom-start" opened={tagsMenuOpened}>
							<Tags>
								{Array.from(selectedTags).map((tag) => (
									<Badge
										key={tag}
										size="sm"
										variant="filled"
										bg={defaultTheme.v3.colors.white[0]}
										c={defaultTheme.v3.colors.gray[9]}
										style={{
											border: `1px solid ${defaultTheme.v3.colors.gray[9]}`,
										}}
									>
										<TagContentDiv>
											<Text size="B6Medium" fz="sm" fw="lighter">
												{
													getEntityClassifierBadgeConfig(tag, tCapManage)
														.label
												}
											</Text>
											<CloseIconButton
												onClick={() => handleTagSelect(tag, false)}
												variant="close"
												size="md"
											/>
										</TagContentDiv>
									</Badge>
								))}
								<Menu.Target>
									<Badge
										size="sm"
										variant="filled"
										bg={defaultTheme.v3.colors.gray[4]}
										c={defaultTheme.v3.colors.dark[4]}
										ref={tagsDropdownTargetRef}
									>
										<AddNewText
											disabled={availableTags.length === 0}
											size="sm"
											fz="sm"
											fw="lighter"
											onClick={(e) => {
												e.stopPropagation();
												setTagsMenuOpened(!tagsMenuOpened);
											}}
										>
											Add new
										</AddNewText>
									</Badge>
								</Menu.Target>
							</Tags>
							<EntitySelectorMenuDropdown>
								<TagsDropdownContainer ref={tagsDropdownRef}>
									<Text size="B6Medium" fz="sm" c="gray.9">
										Add filter
									</Text>

									<Menu.Divider />
									{availableTags.map((tag) => {
										const config = getEntityClassifierBadgeConfig(
											tag,
											tCapManage
										);

										return (
											<TagItemDiv key={tag}>
												<Checkbox
													checked={selectedTags.has(tag)}
													onChange={(e) =>
														handleTagSelect(tag, e.target.checked)
													}
													size="xs"
													style={{
														alignItems: 'center',
														borderRadius: '0.125rem',
													}}
													label={
														<UserSelectionText size="B6Medium" fz="sm">
															{config.label}
														</UserSelectionText>
													}
												/>
											</TagItemDiv>
										);
									})}
								</TagsDropdownContainer>
							</EntitySelectorMenuDropdown>
						</Menu>
					</TagsContainer>
					<Menu.Divider />

					<VirtualListContainer ref={virtualListRef}>
						<VirtualListWrapper $totalHeight={virtualizer.getTotalSize()}>
							{virtualizer.getVirtualItems().map((virtualItem) => {
								const person = personListItems[virtualItem.index];
								return (
									<VirtualListItem
										key={`${person.personId}:${person.mipId ?? ''}`}
										style={{
											position: 'absolute',
											top: 0,
											left: 0,
											width: '100%',
											transform: `translateY(${virtualItem.start}px)`,
											overflow: 'hidden',
										}}
									>
										<EntityButton
											variant="subtle"
											onClick={() => handlePersonSelect(person)}
											$isChecked={person.selected}
											fullWidth
											justify="flex-start"
											style={{
												minWidth: 0,
											}}
											styles={{
												root: {
													width: '100%',
													minWidth: 0,
												},
												inner: {
													width: '100%',
												},
												label: {
													width: '100%',
												},
											}}
										>
											<TruncateMiddle
												text={person.label}
												size="B6Medium"
												color={person.selected ? 'blue.7' : 'inherit'}
												ta="left"
											/>
										</EntityButton>
									</VirtualListItem>
								);
							})}
						</VirtualListWrapper>
					</VirtualListContainer>

					{personListItems.length === 0 && (
						<NoResultsDiv>
							<Text size="B6Medium" fz="sm" c="gray.9">
								No entities found
							</Text>
						</NoResultsDiv>
					)}
				</div>
			</EntitySelectorMenuDropdown>
		</Menu>
	);
}
